import * as React from "react";
import DeviceContext from "../../../service/indentifyDevice";
import { SearchHighLight } from "../../../service/generic/searchHighlight";
import { ESortState, marginSort } from "../generic/marginSort";
import { marginSecNavOptions, marginEqFnoNavigationList, marginBuySellNavigationList, EMarginScreenState } from "../marginConstants";
import { useRouteMatch } from "react-router-dom";
import { IMarginRouteParams } from "../../../types/components/margin/marginTypes";
import Tab from "../../../generic/tab";
import NavigationDropdown from "../../../generic/navigationDropdown";
import MarginSearch from "../generic/marginSearch";
import MarginCollateralTableDsk from "./marginCollateral/marginCollateralTableDsk";
import MarginCollateralTableMob from "./marginCollateral/marginCollateralTableMob";
import MarginCoverOrderTableDsk from "./marginCoverOrder/marginCoverOrderTableDsk";
import MarginCoverOrderTableMob from "./marginCoverOrder/marginCoverOrderTableMob";
import MarginCurrencyTableDsk from "./marginCurrency/marginCurrencyTableDsk";
import MarginCurrencyTableMob from "./marginCurrency/marginCurrencyTableMob";
import MarginFilter from "../generic/marginFilter";
import MarginFnoTableDsk from "./marginFno/marginFnoTableDsk";
import MarginFnoTableMob from "./marginFno/marginFnoTableMob";
import MarginIntraEqTableDsk from "./marginIntraEq/marginIntraEqTableDsk";
import MarginIntraEqTableMob from "./marginIntraEq/marginIntraEqTableMob";
import MarginNavigationTag from "../generic/marginNavigationTag";
import MarginIntraFnoTableDsk from "./marginIntraFno/marginIntraFnoTableDsk";
import MarginIntraFnoTableMob from "./marginIntraFno/marginIntraFnoTableMob";
import MarginMcxTableDsk from "./marginMcx/marginMcxTableDsk";
import MarginMcxTableMob from "./marginMcx/marginMcxTableMob";
import MarginMtfTableDsk from "./marginMtf/marginMtfTableDsk";
import MarginMtfTableMob from "./marginMtf/marginMtfTableMob";
import Loader from "../../../generic/loader";
import SecNavigationDesktop from "../../../generic/SecNavigation/SecNavigationDesktop";
import { IListObj } from "../../../types/genericTypes";

if (DeviceContext().isDesktop) {
	require("../../../../assets/scss/components/toc/marginFiles/marginTables/marginTableDsk.scss");
} else if (DeviceContext().isTablet) {
	require("../../../../assets/scss/components/toc/marginFiles/marginTables/marginTableTab.scss");
} else {
	require("../../../../assets/scss/components/toc/marginFiles/marginTables/marginTableMob.scss");
}

export interface IMarginTableProps {
	data: Array<any>;
	screenState: EMarginScreenState;
}

const MarginTable: React.FC<IMarginTableProps> = (props) => {
	const [filteredData, setFilteredData] = React.useState(props.data);
	const [tableData, setTableData] = React.useState(props.data);
	const [numOfRows, setNumOfRows] = React.useState(10);
	const [sortedColumnState, setSortedColumnState] = React.useState({ colName: null as string | null, sortState: ESortState.ASC });
	const routeParams = useRouteMatch<IMarginRouteParams>();
	const filterFiles: Array<IMarginRouteParams["fileType"]> = ["fno", "currency", "mcx", "ncdex"];
	const tagFiles: Array<IMarginRouteParams["fileType"]> = ["intraday", "mcx", "ncdex"];
	const [searchText, setSearchText] = React.useState("");
	const [filterDate, setFilterDate] = React.useState("all");

	let navigationList: Array<IListObj> = [];
	if (routeParams.params.fileType === "intraday") {
		navigationList = marginEqFnoNavigationList;
	} else {
		navigationList = marginBuySellNavigationList;
	}

	let searchKey = "";
	switch (routeParams.params.fileType) {
		case "intraday":
			if (routeParams.params.subParam === "equity") {
				searchKey = "EdelCode";
			} else if (routeParams.params.subParam === "fno") {
				searchKey = "ScripName";
			}
			break;
		case "collateral":
			searchKey = "EdelCode";
			break;
		case "coverorder":
			searchKey = "Scrip";
			break;
		case "fno":
		case "currency":
		case "mcx":
		case "ncdex":
			searchKey = "trdSym";
			break;
		case "mtf":
			searchKey = "Name";
	}

	const filterKey = "ExpDate";

	function parsePropData() {
		// copy data because we need to pass fresh data in utilHeader
		return props.data.map((row) => ({ ...row }));
	}

	React.useEffect(() => {
		// parse data first to move hdgVl to first level
		const data = parsePropData();

		setFilteredData(data);
		setTableData(data);
		setNumOfRows(10); // because this component is shared by all components. When data is changed, we need to reset load more.
		setSearchText("");
		setFilterDate("all");
	}, [props.data]);

	function onSort(data: Array<any>, sortState: ESortState, colName: string) {
		setSortedColumnState({ colName, sortState });
		setTableData(data);
	}

	function sortAfterFilter(groupedData: Array<any>) {
		// if some column was sorted earlier, then sort that again
		if (sortedColumnState.colName) {
			// copy grouped data, because it will change after sorting
			// filtered data will also change with groupedData
			const copyGroupedData = [...groupedData];

			marginSort(copyGroupedData, sortedColumnState.sortState, sortedColumnState.colName, onSort);
		}
	}

	function onDataSearch(filteredData: Array<any>, sText: string) {
		setSearchText(sText);

		const searchText: string = sText.toLowerCase();

		if (filterDate !== "all") {
			// filter the data depending on what is selected if any date is selected
			// if there is no filter on the screen then this will not work because filterDate will always be "all"
			filteredData = filteredData.filter((d) => d[filterKey] === filterDate);
		}

		// set filtered data before highlighting it because filtered data is used in sorting
		setFilteredData(filteredData);

		// highlight the rows
		filteredData.forEach((row: any) => (row[searchKey] = SearchHighLight(row[searchKey], searchText)));

		setTableData(filteredData);
		sortAfterFilter(filteredData);
	}

	function onDataFilter(filteredData: Array<any>, sText: string) {
		setFilterDate(sText);

		if (searchText) {
			// change search term to lowercase
			const filter = searchText.toLowerCase();

			// filter the data depending on search text if there is any search text in the search box
			filteredData = filteredData.filter((d) => d[searchKey].toLowerCase().includes(filter));

			// set filtered data before highlighting it because filtered data is used in sorting
			setFilteredData(filteredData);

			// highlight the rows
			filteredData.forEach((row: any) => (row[searchKey] = SearchHighLight(row[searchKey], searchText)));
		} else {
			setFilteredData(filteredData);
		}

		setTableData(filteredData);
		sortAfterFilter(filteredData);
	}

	function content() {
		switch (routeParams.params.fileType) {
			case "intraday":
				if (routeParams.params.subParam === "equity") {
					return !DeviceContext().isMobile ? (
						<MarginIntraEqTableDsk
							tableData={tableData}
							filteredData={filteredData}
							numOfRows={numOfRows}
							setTableData={setTableData}
							sortProps={{ onSort, sortedColumnState }}
						/>
					) : (
						<MarginIntraEqTableMob
							tableData={tableData}
							filteredData={filteredData}
							numOfRows={numOfRows}
							setTableData={setTableData}
							sortProps={{ onSort, sortedColumnState }}
						/>
					);
				}
				if (routeParams.params.subParam === "fno") {
					return !DeviceContext().isMobile ? (
						<MarginIntraFnoTableDsk
							tableData={tableData}
							filteredData={filteredData}
							numOfRows={numOfRows}
							setTableData={setTableData}
							sortProps={{ onSort, sortedColumnState }}
						/>
					) : (
						<MarginIntraFnoTableMob
							tableData={tableData}
							filteredData={filteredData}
							numOfRows={numOfRows}
							setTableData={setTableData}
							sortProps={{ onSort, sortedColumnState }}
						/>
					);
				}
				break;
			case "collateral":
				return !DeviceContext().isMobile ? (
					<MarginCollateralTableDsk
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				) : (
					<MarginCollateralTableMob
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				);
			case "coverorder":
				return !DeviceContext().isMobile ? (
					<MarginCoverOrderTableDsk
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				) : (
					<MarginCoverOrderTableMob
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				);
			case "fno":
				return !DeviceContext().isMobile ? (
					<MarginFnoTableDsk
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				) : (
					<MarginFnoTableMob
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				);
			case "currency":
				return !DeviceContext().isMobile ? (
					<MarginCurrencyTableDsk
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				) : (
					<MarginCurrencyTableMob
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				);
			case "mcx":
			case "ncdex":
				return !DeviceContext().isMobile ? (
					<MarginMcxTableDsk
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				) : (
					<MarginMcxTableMob
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				);
			case "mtf":
				return !DeviceContext().isMobile ? (
					<MarginMtfTableDsk
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				) : (
					<MarginMtfTableMob
						tableData={tableData}
						filteredData={filteredData}
						numOfRows={numOfRows}
						setTableData={setTableData}
						sortProps={{ onSort, sortedColumnState }}
					/>
				);
		}
	}

	return DeviceContext().isDesktop ? (
		<section className="outer_container container_desk mrgn_cont_dsk">
			<section className="lyCont">
				<section className="list_view">
					<div className="ly_list_view">
						<div className="lv_menu">
							<SecNavigationDesktop defaultValue={routeParams.params.fileType} list={marginSecNavOptions} />
						</div>
					</div>

					{props.screenState === EMarginScreenState.LOADING ? (
						<div className="ly_right_cont margin_loader_cont">
							<Loader size={20} color="blue" />
						</div>
					) : (
						<div className="ly_right_cont">
							<div className="margin_table">
								<div className="util_cont search_active">
									{tagFiles.includes(routeParams.params.fileType) && (
										<MarginNavigationTag defaultValue={routeParams.params.subParam} tagList={navigationList} />
									)}
									<MarginSearch value={searchText} searchList={parsePropData()} searchKey={searchKey} onSearch={onDataSearch} />
									{filterFiles.includes(routeParams.params.fileType) && (
										<MarginFilter data={parsePropData()} onFilter={onDataFilter} expiryKey={filterKey} />
									)}
								</div>

								{content()}

								<div className="load_more_cont">
									{tableData.length > numOfRows && (
										<button className="load_more" onClick={() => setNumOfRows(Math.min(numOfRows + 10, props.data.length))}>
											<i className="edel-icon-plus" />
											Load All
										</button>
									)}
								</div>
							</div>
						</div>
					)}
				</section>
			</section>
		</section>
	) : DeviceContext().isTablet ? (
		<section className="outer_container container_tab mrgn_cont_tab">
			<section className="lyCont">
				<section className="ob_listing_tab">
					<div className="mrgn_hdr_tab">
						<Tab defaultValue={routeParams.params.fileType} tabList={marginSecNavOptions} position="center" />
					</div>

					{props.screenState === EMarginScreenState.LOADING ? (
						<div className="margin_loader_cont">
							<Loader size={20} color="blue" />
						</div>
					) : (
						<div className="list_details_tab">
							<div className="list_cont_tab">
								<div className="margin_table">
									<div className="util_cont search_active">
										{tagFiles.includes(routeParams.params.fileType) && (
											<MarginNavigationTag defaultValue={routeParams.params.subParam} tagList={navigationList} />
										)}
										<MarginSearch value={searchText} searchList={parsePropData()} searchKey={searchKey} onSearch={onDataSearch} />
										{filterFiles.includes(routeParams.params.fileType) && (
											<MarginFilter data={parsePropData()} onFilter={onDataFilter} expiryKey={filterKey} />
										)}
									</div>

									{content()}

									<div className="load_more_cont">
										{tableData.length > numOfRows && (
											<button className="load_more" onClick={() => setNumOfRows(Math.min(numOfRows + 10, props.data.length))}>
												<i className="edel-icon-plus" />
												Load All
											</button>
										)}
									</div>
								</div>
							</div>
						</div>
					)}
				</section>
			</section>
		</section>
	) : (
		<section className="outer_container container_mob mrgn_cont_mob">
			<section className="lyCont">
				<section className="ob_mob_hdr">
					<div className="margin_title">
						<NavigationDropdown
							defaultValue={routeParams.params.fileType}
							dropdownOptions={marginSecNavOptions}
							onSelect={() => {}}
							type="sec_nav"
						/>

						{props.screenState === EMarginScreenState.DATA && (
							<div>
								{filterFiles.includes(routeParams.params.fileType) && (
									<MarginFilter data={parsePropData()} onFilter={onDataFilter} expiryKey={filterKey} />
								)}
								{tagFiles.includes(routeParams.params.fileType) && (
									<MarginNavigationTag defaultValue={routeParams.params.subParam} tagList={navigationList} />
								)}
							</div>
						)}
					</div>
					{props.screenState === EMarginScreenState.LOADING ? (
						<div className="margin_loader_cont">
							<Loader size={20} color="blue" />
						</div>
					) : (
						<div className="ob_content_mob">
							<div className="margin_table">
								<div className="util_cont search_active">
									<MarginSearch value={searchText} searchList={parsePropData()} searchKey={searchKey} onSearch={onDataSearch} />
								</div>

								{content()}

								<div className="load_more_cont">
									{tableData.length > numOfRows && (
										<button className="load_more" onClick={() => setNumOfRows(Math.min(numOfRows + 10, props.data.length))}>
											<i className="edel-icon-plus" />
											Load All
										</button>
									)}
								</div>
							</div>
						</div>
					)}
				</section>
			</section>
		</section>
	);
};

export default MarginTable;
